import _objectWithoutProperties from 'babel-runtime/helpers/objectWithoutProperties';
import _mergeJSXProps from 'babel-helper-vue-jsx-merge-props';
import _extends from 'babel-runtime/helpers/extends';
import _defineProperty from 'babel-runtime/helpers/defineProperty';
import classNames from 'classnames';
// import * as allIcons from '@ant-design/icons/lib/dist';
// import VueIcon from '@ant-design/icons-vue';
import PropTypes from '../_util/vue-types';
// import createFromIconfontCN from './IconFont';
import { svgBaseProps, withThemeSuffix, removeTypeTheme, getThemeFromTypeName, alias } from './utils';
import warning from '../_util/warning';
import LocaleReceiver from '../locale-provider/LocaleReceiver';
// import { getTwoToneColor, setTwoToneColor } from './twoTonePrimaryColor';
import { filterEmpty, getListeners, mergeProps } from '../_util/props-util';
import Base from '../base';

// Initial setting
// VueIcon.add(...Object.keys(allIcons).map(key => allIcons[key]));
// setTwoToneColor('#1890ff');

// https://github.com/vueComponent/ant-design-vue/issues/2745
// VueIcon.add(
//   ...Object.keys(allIcons)
//     .filter(key => key !== 'default')
//     .map(key => allIcons[key]),
// );
// setTwoToneColor('#1890ff');
var defaultTheme = 'outlined';
var dangerousTheme = void 0;

function renderIcon(h, locale, context) {
  var _classNames;

  var props = context.$props,
      $slots = context.$slots;

  var listeners = getListeners(context);
  var type = props.type,
      Component = props.component,
      viewBox = props.viewBox,
      spin = props.spin,
      theme = props.theme,
      twoToneColor = props.twoToneColor,
      rotate = props.rotate,
      tabIndex = props.tabIndex;

  var children = filterEmpty($slots['default']);
  children = children.length === 0 ? undefined : children;

  warning(Boolean(type || Component || children), 'Icon', 'Icon should have `type` prop or `component` prop or `children`.');

  var classString = classNames((_classNames = {}, _defineProperty(_classNames, 'gdaicon', true), _defineProperty(_classNames, 'gdaicon-' + type, !!type), _classNames));

  var svgClassString = classNames(_defineProperty({}, 'gdaicon-spin', !!spin || type === 'loading'));

  var svgStyle = rotate ? {
    msTransform: 'rotate(' + rotate + 'deg)',
    transform: 'rotate(' + rotate + 'deg)'
  } : undefined;

  var innerSvgProps = {
    attrs: _extends({}, svgBaseProps, {
      viewBox: viewBox
    }),
    'class': svgClassString,
    style: svgStyle
  };
  if (!viewBox) {
    delete innerSvgProps.attrs.viewBox;
  }

  var renderInnerNode = function renderInnerNode() {
    // component > children > type
    if (Component) {
      return h(
        Component,
        innerSvgProps,
        [children]
      );
    }
    if (children) {
      warning(Boolean(viewBox) || children.length === 1 && children[0].tag === 'use', 'Icon', 'Make sure that you provide correct `viewBox`' + ' prop (default `0 0 1024 1024`) to the icon.');
      var _innerSvgProps = {
        attrs: _extends({}, svgBaseProps),
        'class': svgClassString,
        style: svgStyle
      };

      return h(
        'svg',
        _mergeJSXProps([_innerSvgProps, {
          attrs: { viewBox: viewBox }
        }]),
        [children]
      );
    }

    if (typeof type === 'string') {
      var computedType = type;
      if (theme) {
        var themeInName = getThemeFromTypeName(type);
        warning(!themeInName || theme === themeInName, 'Icon', 'The icon name \'' + type + '\' already specify a theme \'' + themeInName + '\',' + (' the \'theme\' prop \'' + theme + '\' will be ignored.'));
      }
      computedType = withThemeSuffix(removeTypeTheme(alias(computedType)), dangerousTheme || theme || defaultTheme);

      return h(VueIcon, {
        attrs: {
          focusable: 'false',

          type: computedType,
          primaryColor: twoToneColor
        },
        'class': svgClassString, style: svgStyle
      });
    }
  };
  var iconTabIndex = tabIndex;
  if (iconTabIndex === undefined && 'click' in listeners) {
    iconTabIndex = -1;
  }
  // functional component not support nativeOn，https://github.com/vuejs/vue/issues/7526
  var iProps = {
    attrs: {
      'aria-label': type && locale.icon + ': ' + type,
      tabIndex: iconTabIndex
    },
    on: listeners,
    'class': classString,
    staticClass: ''
  };
  return h(
    'i',
    iProps,
    [renderInnerNode()]
  );
}

var customCache = new Set();
function createIconfont(options) {
  var prefix = options.prefix,
      scriptUrl = options.scriptUrl,
      _options$extraCommonP = options.extraCommonProps,
      extraCommonProps = _options$extraCommonP === undefined ? {} : _options$extraCommonP;

  /**
   * DOM API required.
   * Make sure in browser environment.
   * The Custom Icon will create a <script/>
   * that loads SVG symbols and insert the SVG Element into the document body.
   */

  if (typeof document !== 'undefined' && typeof window !== 'undefined' && typeof document.createElement === 'function' && typeof scriptUrl === 'string' && scriptUrl.length && !customCache.has(scriptUrl)) {
    var script = document.createElement('script');
    script.setAttribute('src', scriptUrl);
    script.setAttribute('data-namespace', scriptUrl);
    customCache.add(scriptUrl);
    document.body.appendChild(script);
  }

  var Iconfont = {
    functional: true,
    name: 'GIconfont',
    props: Icon.props,
    render: function render(h, context) {
      var props = context.props,
          slots = context.slots,
          listeners = context.listeners,
          data = context.data;

      var type = props.type,
          theme = props.theme,
          restProps = _objectWithoutProperties(props, ['type', 'theme']);

      var slotsMap = slots();
      var children = slotsMap['default'];
      var themeInName = getThemeFromTypeName(type);
      var computedType = withThemeSuffix(removeTypeTheme(type), theme || themeInName || defaultTheme);
      // component > children > type
      var content = null;

      if (type) {
        content = h('use', { attrs: { 'xlink:href': '#' + (prefix ? prefix + '-' : '') + computedType } });
      }

      if (children) {
        content = children;
      }

      var iconProps = mergeProps(extraCommonProps, data, { props: props, on: listeners });

      return h(
        Icon,
        iconProps,
        [content]
      );
    }
  };
  return Iconfont;
}

var Icon = {
  name: 'GIcon',
  props: {
    tabIndex: PropTypes.number,
    type: PropTypes.string,
    component: PropTypes.any,
    viewBox: PropTypes.any,
    spin: PropTypes.bool.def(false),
    rotate: PropTypes.number,
    theme: PropTypes.oneOf(['filled', 'outlined', 'twoTone']),
    twoToneColor: PropTypes.string,
    role: PropTypes.string
  },
  render: function render(h) {
    var _this = this;

    return h(LocaleReceiver, {
      attrs: {
        componentName: 'Icon'
      },
      scopedSlots: { 'default': function _default(locale) {
          return renderIcon(h, locale, _this);
        } }
    });
  }
};

var GIcon = createIconfont({
  prefix: 'gdicon',
  /**
   * 基础 - 稿定图标库
   * https://www.iconfont.cn/manage/index?manage_type=myprojects&projectId=2378633
   */
  scriptUrl: 'https://at.alicdn.com/t/font_2378633_i0berj132t.js'
});

GIcon.createFromIconfontCN = createIconfont;

// Icon.createFromIconfontCN = createIconfont;
// Icon.getTwoToneColor = getTwoToneColor;
// Icon.setTwoToneColor = setTwoToneColor;

/* istanbul ignore next */
GIcon.install = function (Vue) {
  Vue.use(Base);
  Vue.component(Icon.name, GIcon);
};

export default GIcon;